/**
 * Leads — the pipeline, as one list. Spec §H (FR-H1, H2, H6). Redrawn on the
 * list shell in Wave 15E (C14 §10.6 items 1–2, ADR-0033): one table, the stage
 * a filter and a column — **not a board**, by the owner's ruling (ADR-0033,
 * *Alternatives considered*). A lead opens in a dialog (`LeadDialog`); there
 * is no lead page and no address for one.
 *
 * The screen is still arranged around the one thing a firm does with this list
 * every morning: **work the unclaimed enquiries**. Anything that arrived from
 * the website overnight belongs to nobody, and a list sorted by stage would bury
 * it under fifty leads somebody is already on. So the unclaimed ones sort first,
 * a line above the table counts them and narrows the list to them in one click,
 * and each carries its own *Take it*.
 *
 * ## What the UI gate is, and is not
 *
 * FiveOX ADR-0029: enforce at UI **and** API level. So:
 *
 * * `lead:can_create` hides *Add a lead*;
 * * `lead:can_update` hides every stage control, *Take it* and the note box;
 * * `lead:can_delete` hides **merge** — the partner and the practice manager
 *   hold it (spec §P). A merge retires a lead from the pipeline, and retiring
 *   is what `can_delete` means on a resource where nothing is ever erased. It
 *   asks first (ADR-0033 §1), which it did not before Wave 15.
 * * `lead:can_view_all` decides nothing here directly. The *service* narrows
 *   the list for a caller without it, and hands back `meta.narrowed` so this
 *   screen can say "your leads" rather than silently showing a shorter list
 *   than a colleague sees.
 *
 * Every one of those is a hidden control **and** a refusing route. A hidden
 * button alone is not a permission check.
 *
 * ## Search and the stage filter run here, over the newest 200
 *
 * Before Wave 15 the stage and the search went to the service. The list shell
 * narrows what the page holds (C14 §10.6 item 8: no server-side paging), so the
 * page reads the newest 200 — the route's ceiling — and says so when the firm
 * holds more (`meta.total`).
 */

import { Alert, Button, ConfirmDialog, formatDate } from '@fiveox/ui'
import { Inbox, Plus, UserCheck } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import {
  ExportButton,
  type ListColumn,
  ListPage,
  ListTable,
  ListToolbar,
  StatusBadge,
  useIsPhone,
  useListTable,
} from '../../components/page'
import { LEAD, useEvaluation } from '../../lib/access'
import { ApiError } from '../../lib/api'
import {
  LEAD_STAGES,
  type LeadRecord,
  type LeadSource,
  SOURCE_LABELS,
  STAGE_LABELS,
  arrivalNote,
  canMove,
  crm,
  leadSubtitle,
  listMeta,
} from '../../lib/api/crm'
import { services as catalog, type Service } from '../../lib/api/services'
import { AddLeadDialog } from './AddLeadDialog'
import { LeadDialog } from './LeadDialog'

/** The route's ceiling (`limit` le=200). */
const PAGE = 200

const SOURCES: LeadSource[] = ['site_form', 'self_registration', 'staff']

/** Unclaimed first, then newest first — the order the firm works them in. */
function workOrder(a: LeadRecord, b: LeadRecord): number {
  const claimed = Number(Boolean(a.owner_user_id)) - Number(Boolean(b.owner_user_id))
  if (claimed !== 0) return claimed
  return b.created_at.localeCompare(a.created_at)
}

/** A merge waiting on its confirmation: `other` is retired into `into`. */
interface Merging {
  other: LeadRecord
  into: LeadRecord
}

export function Leads() {
  const { can } = useEvaluation()
  const phone = useIsPhone()
  const mayCreate = can(LEAD, 'can_create')
  const mayUpdate = can(LEAD, 'can_update')
  const mayMerge = can(LEAD, 'can_delete')

  const [rows, setRows] = useState<LeadRecord[]>([])
  const [total, setTotal] = useState(0)
  const [narrowed, setNarrowed] = useState(false)
  const [serviceNames, setServiceNames] = useState<Record<string, string>>({})
  const [open, setOpen] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const [merging, setMerging] = useState<Merging | null>(null)
  const [mergeBusy, setMergeBusy] = useState(false)
  const [mergeError, setMergeError] = useState<string | null>(null)
  const [taking, setTaking] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    setError(null)
    try {
      const body = await crm.list({ limit: PAGE })
      const meta = listMeta(body.meta)
      setRows([...body.data].sort(workOrder))
      setTotal(meta.total)
      setNarrowed(meta.narrowed)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Leads could not be loaded.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  // The catalog, once, so a lead's `service_id` renders as a name. Failure is
  // silent and the lead still renders — a missing service name must not take
  // the pipeline down with it.
  useEffect(() => {
    catalog
      .list()
      .then((body) =>
        setServiceNames(Object.fromEntries(body.data.services.map((s: Service) => [s.id, s.name]))),
      )
      .catch(() => setServiceNames({}))
  }, [])

  const take = async (lead: LeadRecord) => {
    setTaking(lead.id)
    setError(null)
    try {
      // No user id crosses the wire: the service reads the owner from the
      // session (`POST /leads/{id}/claim`), so "take this one" cannot be edited
      // into "give this one to somebody else".
      await crm.claim(lead.id)
      await load()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That lead could not be taken.')
    } finally {
      setTaking(null)
    }
  }

  const merge = async () => {
    if (!merging) return
    setMergeBusy(true)
    setMergeError(null)
    try {
      await crm.merge(merging.other.id, merging.into.id)
      const survivor = merging.into.id
      setMerging(null)
      setOpen(survivor)
      await load()
    } catch (err) {
      setMergeError(err instanceof ApiError ? err.message : 'Those leads could not be merged.')
    } finally {
      setMergeBusy(false)
    }
  }

  const serviceName = (lead: LeadRecord) => (lead.service_id ? serviceNames[lead.service_id] : null)

  const columns = useMemo<ListColumn<LeadRecord>[]>(
    () => [
      {
        key: 'full_name',
        label: 'Name',
        width: 260,
        required: true,
        phone: true,
        sortField: 'full_name',
        exportValue: (lead) => lead.full_name,
        render: (lead) => (
          <div className="min-w-0">
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation()
                setOpen(lead.id)
              }}
              className="text-foreground hover:text-primary focus-visible:ring-primary/40 rounded text-left font-medium focus-visible:ring-2 focus-visible:outline-none"
            >
              {lead.full_name}
            </button>
            <p className="text-muted-foreground max-w-[8.5rem] truncate text-xs sm:max-w-[15rem]">
              {leadSubtitle(lead, serviceName(lead)) || arrivalNote(lead) || '—'}
            </p>
            {lead.stage === 'lost' && lead.lost_reason && (
              <p className="text-muted-foreground max-w-[8.5rem] truncate text-xs sm:max-w-[15rem]">
                Lost: {lead.lost_reason}
              </p>
            )}
          </div>
        ),
      },
      {
        key: 'status',
        label: 'Stage',
        width: 130,
        phone: true,
        sortField: 'stage',
        exportValue: (lead) => STAGE_LABELS[lead.stage],
        render: (lead) => <StatusBadge kind="lead" status={lead.stage} />,
      },
      {
        key: 'owner',
        label: 'Who has it',
        width: 150,
        sortField: 'owner_user_id',
        exportValue: (lead) => (lead.owner_user_id ? 'Taken' : 'Waiting for someone'),
        render: (lead) =>
          lead.owner_user_id ? (
            <span className="text-muted-foreground">Taken</span>
          ) : (
            <span className="text-foreground font-medium">Waiting for someone</span>
          ),
      },
      {
        key: 'source',
        label: 'Came from',
        width: 150,
        sortField: 'source',
        exportValue: (lead) => SOURCE_LABELS[lead.source],
        render: (lead) => <span className="text-muted-foreground">{SOURCE_LABELS[lead.source]}</span>,
      },
      {
        key: 'created_at',
        label: 'Arrived',
        width: 120,
        sortField: 'created_at',
        exportValue: (lead) => lead.created_at.slice(0, 10),
        render: (lead) => (
          <span className="text-muted-foreground whitespace-nowrap">{formatDate(lead.created_at)}</span>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [serviceNames],
  )

  // How to reach them feeds the sheet and never the table, which carries it
  // in the name's second line.
  const sheet = useMemo<ListColumn<LeadRecord>[]>(
    () => [
      ...columns,
      ...(
        [
          ['email', 'Email', (lead: LeadRecord) => lead.email ?? ''],
          ['mobile', 'Telephone', (lead: LeadRecord) => lead.mobile ?? ''],
          ['organisation_name', 'Business', (lead: LeadRecord) => lead.organisation_name ?? ''],
        ] as const
      ).map(([key, label, value]) => ({ key, label, width: 0, exportValue: value, render: () => null })),
    ],
    [columns],
  )

  const counts = useMemo(() => {
    const out: Partial<Record<string, number>> = {}
    for (const lead of rows) out[lead.stage] = (out[lead.stage] ?? 0) + 1
    return out
  }, [rows])

  const list = useListTable(rows, {
    columns,
    search: (lead) => [
      lead.full_name,
      lead.email,
      lead.mobile,
      lead.organisation_name,
      lead.service_label,
      serviceName(lead),
    ],
    filters: [
      {
        key: 'stage',
        label: 'Stage',
        allLabel: 'Every stage',
        options: LEAD_STAGES.map((stage) => ({
          value: stage,
          label: `${STAGE_LABELS[stage]} (${counts[stage] ?? 0})`,
        })),
        match: (lead, value) => lead.stage === value,
      },
      {
        key: 'owner',
        label: 'Who has it',
        allLabel: 'Anyone or nobody',
        options: [
          { value: 'waiting', label: 'Waiting for someone' },
          { value: 'taken', label: 'Taken' },
        ],
        match: (lead, value) => (value === 'waiting') === !lead.owner_user_id,
      },
      {
        key: 'source',
        label: 'Came from',
        allLabel: 'Anywhere',
        options: SOURCES.map((source) => ({ value: source, label: SOURCE_LABELS[source] })),
        match: (lead, value) => lead.source === value,
      },
    ],
  })

  const waiting = rows.filter((lead) => !lead.owner_user_id && canMove(lead)).length
  const ownerFilter = list.filters.find((f) => f.def.key === 'owner')

  const addButton = mayCreate ? (
    <Button onClick={() => setAdding(true)}>
      <Plus size={16} aria-hidden /> Add a lead
    </Button>
  ) : undefined

  return (
    <>
      <ListPage
        title="Leads"
        description={
          narrowed
            ? 'The leads you own, and the ones nobody has taken yet.'
            : 'Everyone who has asked your firm for something and is not a client yet.'
        }
        primary={addButton}
        loading={loading}
        error={error}
        isEmpty={list.isEmpty}
        empty={{
          title: 'No leads yet',
          description:
            'Enquiries from your website’s contact form arrive here on their own. You can also add somebody who called or walked in.',
          action: addButton,
        }}
        toolbar={
          <ListToolbar
            list={list}
            placeholder="Search by name, email, telephone or business"
            actions={<ExportButton rows={list.sorted} columns={sheet} filename="prodesk-leads" />}
          />
        }
      >
        {waiting > 0 && ownerFilter?.value !== 'waiting' && (
          <div className="border-border bg-card flex flex-wrap items-center gap-3 rounded-2xl border px-4 py-3 shadow-sm">
            <span className="bg-primary/10 text-primary flex h-9 w-9 shrink-0 items-center justify-center rounded-lg">
              <Inbox size={18} aria-hidden />
            </span>
            <p className="text-foreground min-w-0 flex-1 text-sm">
              <span className="font-medium">
                {waiting} {waiting === 1 ? 'enquiry is' : 'enquiries are'} waiting for someone.
              </span>{' '}
              <span className="text-muted-foreground hidden sm:inline">
                Nobody has taken {waiting === 1 ? 'it' : 'them'} yet — they sort to the top.
              </span>
            </p>
            <Button variant="outline" size="sm" onClick={() => ownerFilter?.set('waiting')}>
              Show only those
            </Button>
          </div>
        )}
        {total > rows.length && (
          <Alert tone="info">
            You have {total} leads; this list holds the newest {rows.length}. Search and the
            filters look only at those.
          </Alert>
        )}
        <ListTable
          list={list}
          rowKey={(lead) => lead.id}
          onRowClick={(lead) => setOpen(lead.id)}
          trailing={
            mayUpdate
              ? {
                  header: <span className="sr-only">Take</span>,
                  width: phone ? 60 : 128,
                  cell: (lead) =>
                    !lead.owner_user_id && canMove(lead) ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="whitespace-nowrap"
                        loading={taking === lead.id}
                        disabled={taking !== null}
                        onClick={(event) => {
                          event.stopPropagation()
                          void take(lead)
                        }}
                      >
                        <UserCheck size={14} aria-hidden />
                        <span className="sr-only sm:not-sr-only">Take it</span>
                      </Button>
                    ) : null,
                }
              : undefined
          }
        />
      </ListPage>

      {open && !merging && (
        <LeadDialog
          leadId={open}
          mayUpdate={mayUpdate}
          mayMerge={mayMerge}
          serviceNames={serviceNames}
          onClose={() => setOpen(null)}
          onChanged={load}
          onMerge={(other, into) => {
            setMergeError(null)
            setMerging({ other, into })
          }}
        />
      )}

      <ConfirmDialog
        open={merging !== null}
        onClose={() => setMerging(null)}
        title="Merge these leads?"
        confirmLabel="Merge"
        cancelLabel="Keep them apart"
        onConfirm={() => void merge()}
        busy={mergeBusy}
        busyLabel="Merging…"
        error={mergeError}
      >
        {merging && (
          <>
            <p>
              <span className="text-foreground font-medium">{merging.other.full_name}</span> is
              folded into{' '}
              <span className="text-foreground font-medium">{merging.into.full_name}</span>. Its
              trail joins this one and it leaves your pipeline.
            </p>
            <p>Nothing is deleted — both records and both trails are kept.</p>
          </>
        )}
      </ConfirmDialog>

      <AddLeadDialog
        open={adding}
        onClose={() => setAdding(false)}
        onDone={() => {
          setAdding(false)
          void load()
        }}
      />
    </>
  )
}
